import {
  NO_BUILDING_SELECTED,
  type DesignMapCameraState,
} from '../store/design.store';

export const DESIGN_SELECTED_SEARCH_PARAM = 'designSelected';
export const DESIGN_STOREY_SEARCH_PARAM = 'designStorey';
export const DESIGN_CAMERA_SEARCH_PARAM = 'designCamera';

export type DesignSelectionUrlState = {
  selectedBuildingId: string | undefined;
  selectedStoreyId: string | undefined;
  isBuildingOutlineSelected: boolean;
};

/** Read Design selection from the URL without inventing an initial selection. */
export const parseDesignSelectionUrlState = (
  selectedBuildingId: string | null,
  selectedStoreyId: string | null,
): DesignSelectionUrlState => {
  const buildingId = selectedBuildingId || undefined;
  const storeyId = selectedStoreyId || undefined;

  if (buildingId === NO_BUILDING_SELECTED) {
    return {
      selectedBuildingId: NO_BUILDING_SELECTED,
      selectedStoreyId: undefined,
      isBuildingOutlineSelected: false,
    };
  }

  return {
    selectedBuildingId: buildingId,
    selectedStoreyId: storeyId,
    isBuildingOutlineSelected: Boolean(buildingId && !storeyId),
  };
};

/** Compact, lossless camera payload suitable for a shareable query param. */
export const serializeDesignCamera = (
  camera: DesignMapCameraState | undefined,
): string | undefined => {
  if (!camera) return undefined;
  const mapboxCamera = [
    ...camera.center,
    camera.zoom,
    camera.pitch,
    camera.bearing,
    camera.padding.top,
    camera.padding.right,
    camera.padding.bottom,
    camera.padding.left,
  ];
  const hasFacadeState =
    camera.facadeModeActive === true ||
    Boolean(camera.facadeTransitionProgress) ||
    Boolean(camera.facadeVerticalOffsetPixels);
  if (!hasFacadeState) return mapboxCamera.join(',');
  return [
    ...mapboxCamera,
    camera.facadeModeActive ? 1 : 0,
    camera.facadeTransitionProgress ?? 0,
    camera.facadeVerticalOffsetPixels ?? 0,
  ].join(',');
};

/** Reject malformed or unsafe camera values rather than handing them to Mapbox. */
export const parseDesignCamera = (
  value: string | null,
): DesignMapCameraState | undefined => {
  if (!value) return undefined;
  const values = value.split(',').map(Number);
  if (
    (values.length !== 9 && values.length !== 12) ||
    values.some((entry) => !Number.isFinite(entry))
  ) {
    return undefined;
  }

  const [
    lng,
    lat,
    zoom,
    pitch,
    bearing,
    top,
    right,
    bottom,
    left,
    facadeMode,
    facadeTransitionProgress,
    facadeVerticalOffsetPixels,
  ] = values;
  if (
    lng === undefined ||
    lat === undefined ||
    zoom === undefined ||
    pitch === undefined ||
    bearing === undefined ||
    top === undefined ||
    right === undefined ||
    bottom === undefined ||
    left === undefined ||
    lng < -180 ||
    lng > 180 ||
    lat < -90 ||
    lat > 90 ||
    zoom < 0 ||
    zoom > 24 ||
    pitch < 0 ||
    pitch > 85 ||
    [top, right, bottom, left].some((padding) => padding < 0) ||
    (values.length === 12 &&
      (facadeMode === undefined ||
        (facadeMode !== 0 && facadeMode !== 1) ||
        facadeTransitionProgress === undefined ||
        facadeTransitionProgress < 0 ||
        facadeTransitionProgress > 1 ||
        facadeVerticalOffsetPixels === undefined))
  ) {
    return undefined;
  }

  const camera: DesignMapCameraState = {
    center: [lng, lat],
    zoom,
    pitch,
    bearing,
    padding: { top, right, bottom, left },
  };
  if (values.length === 12) {
    camera.facadeModeActive = facadeMode === 1;
    camera.facadeTransitionProgress = facadeTransitionProgress;
    camera.facadeVerticalOffsetPixels = facadeVerticalOffsetPixels;
  }
  return camera;
};
